import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useId,
  useRef
} from "react";
import useOnclickOutside from "react-cool-onclickoutside";
import { Portal } from "reakit/Portal";
import { ITeam, ResGetTeams, TacticApiClient } from "@gettactic/api";
import { classNames } from "@gettactic/helpers/src/css/classNames";
import Link from "next/link";
import { Checkbox } from "@cmp-src/primitives/checkbox";

type Props = {
  open: boolean;
  setOpen: Dispatch<React.SetStateAction<boolean>>;
  hasAnyFav: boolean;
  setPopperElement: Dispatch<SetStateAction<HTMLElement | null>>;
  setArrowElement: Dispatch<SetStateAction<HTMLElement | null>>;
  styles: { [p: string]: React.CSSProperties };
  attributes: { [p: string]: { [p: string]: string } | undefined };
  referenceElement: HTMLElement | null;
  addUserToDefaultGroup: (api: TacticApiClient) => void;
  removeUserFromDefaultGroup: (api: TacticApiClient) => void;
  addUserToGroup: (p: ITeam, api: TacticApiClient) => void;
  removeUserFromGroup: (p: ITeam, api: TacticApiClient) => void;
  groups: undefined | ResGetTeams;
  userGroups: undefined | ResGetTeams;
  api: TacticApiClient;
};

export function FavsDetails({
  setOpen,
  setPopperElement,
  setArrowElement,
  open,
  styles,
  attributes,
  referenceElement,
  hasAnyFav,
  addUserToDefaultGroup,
  removeUserFromDefaultGroup,
  addUserToGroup,
  removeUserFromGroup,
  groups,
  userGroups,
  api
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  // Every row of a list mounts its own picker, so the checkbox ids need a per-picker prefix
  const idPrefix = useId();
  const refToOpen = useRef(referenceElement);
  refToOpen.current = referenceElement;
  const close = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  useOnclickOutside(close, {
    refs: [ref as never, refToOpen as never]
  });

  return (
    <Portal>
      <div
        className={classNames(!open ? "hidden" : "")}
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
      >
        <div
          className={classNames(
            !open ? "hidden opacity-0" : " opacity-100 m-2 bg-white",
            "rounded-lg border bg-white shadow-lg transition duration-300 ease-in-out"
          )}
        >
          <div
            ref={setArrowElement}
            style={styles.arrow}
            {...attributes.arrow}
          />
          <div className="my-2" ref={ref}>
            <h4 className="border-b border-neutral-200 text-base leading-relaxed text-neutral-900 pb-2.5 ">
              <span className="px-3 text-sm text-neutral-900">Add to...</span>
            </h4>
            <ul className="mt-3 px-2 ">
              {!groups || groups.elements.length === 0 ? (
                <li className="flex items-center space-x-2 text-sm">
                  <Checkbox
                    id={`${idPrefix}-default`}
                    checked={hasAnyFav}
                    onCheckedChange={() => {
                      if (hasAnyFav) {
                        removeUserFromDefaultGroup(api);
                      } else {
                        addUserToDefaultGroup(api);
                      }
                    }}
                  />
                  <label htmlFor={`${idPrefix}-default`}>My Favorites</label>
                </li>
              ) : (
                groups.elements.map((group) => {
                  const isInGroup =
                    !!userGroups &&
                    !!userGroups.elements.find((x) => x.id === group.id);
                  return (
                    <li
                      key={group.id}
                      className="flex items-center space-x-2 text-sm"
                    >
                      <Checkbox
                        id={`${idPrefix}-${group.id}`}
                        checked={isInGroup}
                        onCheckedChange={async () => {
                          if (isInGroup) {
                            await removeUserFromGroup(group, api);
                          } else {
                            await addUserToGroup(group, api);
                          }
                        }}
                      />
                      <label htmlFor={`${idPrefix}-${group.id}`}>
                        {group.name}
                      </label>
                    </li>
                  );
                })
              )}
            </ul>

            <div className="link mt-4 px-2 text-content text-sm underline">
              <Link className="" href="/settings/favorites">
                Visit My Favorites Lists
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
